/**
 * Selector de etiquetas de almacén.
 *
 * Tres tipos —lote, ubicación y artículo— con el mismo gesto: marcar lo que
 * se quiere y abrir la hoja para imprimir. El papel es el mismo que el de
 * las etiquetas de mantenimiento (A4 de 3 × 8 a 70 × 37 mm).
 *
 * Los lotes vienen ordenados con los bloqueados y caducados primero: son
 * exactamente los que hay que ir a buscar a la estantería, y ponerlos
 * alfabéticos los escondería entre los demás.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSession } from '../../session/session-context.js';
import { openDownload } from '../../session/open-download.js';
import { ApiError } from '../../session/api-error.js';

export type LabelKind = 'lotes' | 'ubicaciones' | 'articulos';

interface Candidate {
  id: string;
  label: string;
  hint: string | null;
}

/** Un aviso que hay que ver de lejos frente a un dato más. */
const URGENTE = new Set(['BLOQUEADO', 'CADUCADO']);

export function LabelPicker({ kind, help }: { kind: LabelKind; help: string }): React.ReactNode {
  const { api } = useSession();
  const [items, setItems] = useState<Candidate[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<{ items: Candidate[] }>(`/inventory/labels/${kind}/candidates`);
      setItems(data.items);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar la lista.');
    } finally {
      setLoading(false);
    }
  }, [api, kind]);

  useEffect(() => {
    setChosen([]);
    void load();
  }, [load]);

  function toggle(id: string): void {
    setChosen((previous) =>
      previous.includes(id) ? previous.filter((x) => x !== id) : [...previous, id],
    );
  }

  return (
    <>
      <p className="screen__note">{help}</p>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      {loading && <p className="table__loading">Cargando…</p>}

      {!loading && items.length === 0 && (
        <p className="table__empty">Todavía no hay nada de este tipo que etiquetar.</p>
      )}

      {items.length > 0 && (
        <>
          <div className="picker__actions">
            <button
              className="button button--primary"
              type="button"
              disabled={chosen.length === 0}
              onClick={() =>
                // Pestaña nueva: es una hoja para imprimir, no una pantalla
                // donde se sigue trabajando.
                void openDownload(
                  api,
                  `/v1/inventory/labels/${kind}/print?ids=${chosen.join(',')}`,
                )
              }
            >
              Imprimir {chosen.length > 0 ? `(${chosen.length})` : ''}
            </button>
            <button
              className="button button--quiet"
              type="button"
              onClick={() => setChosen(chosen.length === items.length ? [] : items.map((i) => i.id))}
            >
              {chosen.length === items.length ? 'Quitar la selección' : 'Seleccionar todo'}
            </button>
          </div>

          <div className="scroller">
            <ul className="picker">
              {items.map((item) => (
                <li key={item.id}>
                  <label className="picker__row">
                    <input
                      type="checkbox"
                      checked={chosen.includes(item.id)}
                      onChange={() => toggle(item.id)}
                    />
                    <span className="picker__label">{item.label}</span>
                    {item.hint !== null && (
                      <span
                        className={`pill ${URGENTE.has(item.hint) ? 'pill--bad' : 'pill--info'}`}
                      >
                        {item.hint}
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </>
  );
}
